// Assign Tests To Item → "Tests" tab: row model, legacy save rules and the wire
// shape. Kept apart from ItemTestsGrid.tsx so the component file exports only the
// component (fast refresh) and the rules are testable on their own.

/** Legacy messages (AssignTestToItemController) — the server sends the same ones. */
export const MSG_ADD_TEST = 'Add Test';
export const MSG_ENTER_MATRIX = 'Please enter the matrix.';
export const MSG_DUPLICATE_TEST = 'The same Test cannot be assigned to this Item more than once.';
export const MSG_BAD_AMOUNT = 'Please enter a valid Amount.';

/** A detail child area whose edits are held on the client and committed by the
 *  header Save: the shared detail page validates it first and merges its payload. */
export interface BufferedChildHandle {
  /** The message that blocks the save, or null when the rows are valid. */
  validate: () => string | null;
  /** Keys merged into the header PUT body. */
  payload: () => Record<string, unknown>;
}

/** One Tests-tab row as held on screen. `oid` is null for a row added in this edit. */
export interface TestRow {
  key: string;
  oid: string | null;
  testMethodOid: string;
  matrix: string;
  test: string;
  method: string;
  amount: string;
  unitOid: string;
  unitName: string;
}

/** One row of GET /assign-tests-to-item/{id}/tests. */
export interface ApiTestRow {
  oid: string;
  testMethodOid?: string | null;
  matrix?: string | null;
  test?: string | null;
  method?: string | null;
  amount?: number | null;
  unitOid?: string | null;
  unitName?: string | null;
}

/** One row of GET /assign-tests-to-item/test-methods — a choice in the "Test Method"
 *  popup. The popup shows the method NUMBER; the Tests grid shows the method NAME. */
export interface TestMethodOption {
  id: string;
  matrix: string;
  test: string;
  methodNumber: string;
  methodName: string;
}

/** The popup's Search box: matches what the popup SHOWS (Matrix · Test · Method number),
 *  case-insensitively; blank shows everything. */
export function filterTestMethods(options: TestMethodOption[], search: string): TestMethodOption[] {
  const q = search.trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) => `${o.matrix} ${o.test} ${o.methodNumber}`.toLowerCase().includes(q));
}

/** Single-object lookup ticks (legacy XAF lookup popup): ticking a row replaces the
 *  previous tick, unticking leaves what is left, and a header "select all" — several
 *  rows at once — is ignored (returns null = keep the current selection). */
export function singleSelection<K>(current: ReadonlySet<K>, next: ReadonlySet<K>): Set<K> | null {
  const added = [...next].filter((id) => !current.has(id));
  if (added.length > 1) return null;
  return added.length === 1 ? new Set(added) : new Set(next);
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** The legacy grid shows the decimal Amount as currency ($0.00); blank reads as 0. */
export function fmtAmount(amount: string): string {
  const n = amount.trim() === '' ? 0 : Number(amount);
  return Number.isFinite(n) ? usd.format(n) : amount;
}

/** Round to the cent, half away from zero. Shifting the decimal point in the STRING
 *  ("2.345e2") avoids the binary error of `2.345 * 100` (234.49999…), which would
 *  round 2.345 down to 2.34. */
const toCents = (n: number): string => {
  const cents = Math.round(Number(`${Math.abs(n)}e2`));
  return (Number.isFinite(cents) ? (Math.sign(n) * cents) / 100 : n).toFixed(2);
};

/** What may be typed into the Amount editor: digits, one '.', and a leading '-'.
 *  The '$' and ',' of a pasted "$1,234.50" are dropped. */
export function cleanAmountInput(raw: string): string {
  const s = raw.replace(/[^0-9.-]/g, '');
  const sign = s.startsWith('-') ? '-' : '';
  const body = s.replace(/-/g, '');
  const dot = body.indexOf('.');
  return sign + (dot < 0 ? body : body.slice(0, dot + 1) + body.slice(dot + 1).replace(/\./g, ''));
}

/** The amount while it is being edited: a plain number to the cent (blank edits as 0.00). */
export function editableAmount(amount: string): string {
  const n = amount.trim() === '' ? 0 : Number(amount);
  return Number.isFinite(n) ? toCents(n) : amount;
}

/** Leaving the editor: round to the cent; an unfinished entry (a lone '-' or '.', or
 *  nothing at all) reads as 0 — the legacy currency editor never holds a blank. */
export function normalizeAmount(amount: string): string {
  const n = Number(amount);
  return amount.trim() !== '' && Number.isFinite(n) ? toCents(n) : '0.00';
}

/** The ▲ / ▼ spin (and ArrowUp / ArrowDown): one whole unit per step, as the legacy spin editor. */
export function stepAmount(amount: string, delta: number): string {
  const n = amount.trim() === '' ? 0 : Number(amount);
  return toCents((Number.isFinite(n) ? n : 0) + delta);
}

export function fromApiRow(r: ApiTestRow): TestRow {
  return {
    key: r.oid,
    oid: r.oid,
    testMethodOid: r.testMethodOid ?? '',
    matrix: r.matrix ?? '',
    test: r.test ?? '',
    method: r.method ?? '',
    amount: r.amount == null ? '' : String(r.amount),
    unitOid: r.unitOid ?? '',
    unitName: r.unitName ?? '',
  };
}

/** The legacy save rules, checked here for UX — the server checks them again. */
export function validateTestRows(rows: TestRow[]): string | null {
  if (rows.length === 0) return MSG_ADD_TEST;
  if (rows.some((r) => !r.testMethodOid)) return MSG_ENTER_MATRIX;
  if (new Set(rows.map((r) => r.testMethodOid)).size !== rows.length) return MSG_DUPLICATE_TEST;
  if (rows.some((r) => r.amount.trim() !== '' && !Number.isFinite(Number(r.amount)))) return MSG_BAD_AMOUNT;
  return null;
}

/** Wire shape of one row: ids or null (never ''), Amount a number or null. */
export function toPayloadRow(r: TestRow) {
  return {
    oid: r.oid,
    testMethodOid: r.testMethodOid || null,
    amount: r.amount.trim() === '' ? null : Number(r.amount),
    unitOid: r.unitOid || null,
  };
}
